import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import callable from '../../utils/callable'
import { ToastManager } from '../../utils/toastManager'
import { APPLICATION_STRINGS } from '../../strings'

import { getCurrentMenu } from '../../selectors/menus'
import { getAttendantsMap, getEmployeeIds, getManagerIds } from '../../selectors/attendant'
import {
  getManagerApprovalButtonText,
  getManagerApprovalOnFailure,
  getManagerApprovalOnSuccess,
  showManagerApproval,
} from '../Selectors'
import { hideManagerApproval } from '../ActionCreators'
import { setManagerId } from '../../actions/attendant'
import UserLoginComponent from '../../components/userLogin/Component'
import useManagerCodes from '../hooks/useManagerCodes'

const VNManagerPin = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const { getManagerApprovalCodes } = useManagerCodes()
  const approvalCodeToManagerMap = useMemo(() => getManagerApprovalCodes(), [getManagerApprovalCodes])
  const approvalCodes = useMemo(() => Object.keys(approvalCodeToManagerMap || {}), [approvalCodeToManagerMap])
  const employeeIds = useSelector(getEmployeeIds)
  const managerIds = useSelector(getManagerIds)
  const menu = useSelector(getCurrentMenu)
  const attendants = Object.values(useSelector(getAttendantsMap) || {})
  const isOpen = useSelector(showManagerApproval)
  const success = useSelector(getManagerApprovalOnSuccess)
  const failure = useSelector(getManagerApprovalOnFailure)
  const managerApprovalButtonText = useSelector(getManagerApprovalButtonText)

  useEffect(() => {
    if (!isOpen) return
    // We need this component to close itself everytime the user navigates
    const unlisten = history.listen(() => dispatch(hideManagerApproval()))

    return () => {
      unlisten()
    }
  }, [dispatch, history, isOpen])

  const getManagerUuidByEmployeeId = useCallback(
    (employeeId) => attendants?.find((employee) => employee.employeeId === employeeId)?.uuid || null,
    [attendants],
  )

  const submitHandler = useCallback(
    (pin, _, restartScanner) => {
      // pin can be the employee id if the user scanned a badge
      if (managerIds.includes(pin)) {
        // pin matches employee ID of a manager
        const employeeUuid = getManagerUuidByEmployeeId(pin)
        dispatch(setManagerId(employeeUuid))
        callable(success, employeeUuid)
        dispatch(hideManagerApproval())
        return
      }

      // failed to match any manager pin
      if (!approvalCodeToManagerMap[pin]) {
        callable(failure)
        callable(restartScanner)
        ToastManager.error('Invalid Approval Code, Please Try Again', {
          autoClose: 2500,
        })
        return
      }

      // success
      dispatch(setManagerId(approvalCodeToManagerMap[pin]))
      callable(success, approvalCodeToManagerMap[pin])
      dispatch(hideManagerApproval())
    },
    [approvalCodeToManagerMap, dispatch, failure, getManagerUuidByEmployeeId, managerIds, success],
  )

  if (!isOpen) return null

  return (
    <UserLoginComponent
      standName={menu?.standName}
      handleSubmit={submitHandler}
      hamburgerUnlocked={true}
      header={APPLICATION_STRINGS.MANAGER_PIN_HEADER}
      forManagerApproval
      employeeIds={employeeIds}
      approvalCodes={approvalCodes}
      buttonText={managerApprovalButtonText}
    />
  )
}

export default VNManagerPin
